import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { toast } from "sonner";
import { Building2, Mail, Lock, Shield } from "lucide-react";
import { extractErrorMessage } from "@/lib/errorMessage";

export default function LoginPage() {
  const navigate = useNavigate();
  const { login, getDashboardPath } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  
  // Login form state
  const [loginEmail, setLoginEmail] = useState("");
  const [loginPassword, setLoginPassword] = useState("");

  const handleLogin = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    
    try {
      const user = await login(loginEmail, loginPassword);
      toast.success(`Bem-vindo, ${user.name}!`);
      navigate(getDashboardPath(user.role));
    } catch (error) {
      toast.error(extractErrorMessage(error, "Erro ao fazer login"));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex" data-testid="login-page">
      {/* Left Panel - Branding */}
      <div className="hidden lg:flex lg:w-1/2 bg-luvcorp-navy flex-col items-center justify-center p-12 relative overflow-hidden">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-0 w-64 h-64 border border-white/20 rotate-45 -translate-x-1/2 -translate-y-1/2"></div>
          <div className="absolute bottom-0 right-0 w-96 h-96 border border-white/20 rotate-45 translate-x-1/2 translate-y-1/2"></div>
          <div className="absolute top-1/2 left-1/2 w-80 h-80 border border-white/20 -translate-x-1/2 -translate-y-1/2"></div>
        </div>
        
        {/* Logo */}
        <div className="relative z-10 text-center">
          <div className="flex items-center justify-center mb-8">
            <img src="/brand/obrasul-logo.jpeg" alt="OBRASUL-RJ" className="h-24 w-24 rounded-lg object-cover shadow-2xl ring-2 ring-luvcorp-gold/30" />
          </div>
          <h1 className="text-4xl font-heading font-bold text-white mb-2" data-testid="login-brand-name">
            OBRA<span className="text-luvcorp-gold">SUL</span>-RJ
          </h1>
          <p className="text-base text-white/80 font-heading tracking-wider">Obra Padrão</p>
          <p className="text-sm text-white/60 font-heading tracking-wider mt-0.5">SISTEMA DE GESTÃO DE OBRAS</p>
          <div className="mt-8 w-24 h-1 bg-luvcorp-gold mx-auto"></div>
          <p className="mt-8 text-white/60 text-sm max-w-sm mx-auto leading-relaxed">
            Gestão completa de obras, contratos, equipes, patrimônio e estoque com precisão e confiabilidade.
          </p>
        </div>
        
        {/* Bottom decoration */}
        <div className="absolute bottom-8 left-0 right-0 flex justify-center gap-4">
          <div className="w-2 h-2 bg-luvcorp-gold/60 rounded-full"></div>
          <div className="w-2 h-2 bg-luvcorp-gold/40 rounded-full"></div>
          <div className="w-2 h-2 bg-luvcorp-gold/20 rounded-full"></div>
        </div>
      </div>

      {/* Right Panel - Login Form */}
      <div className="flex-1 flex items-center justify-center p-8 bg-neu-base">
        <div className="w-full max-w-md">
          {/* Mobile Logo */}
          <div className="lg:hidden text-center mb-8">
            <div className="inline-flex items-center gap-3">
              <img src="/brand/obrasul-logo.jpeg" alt="OBRASUL-RJ" className="h-12 w-12 rounded-neu-sm object-cover shadow-neu-outset-sm" />
              <div>
                <h1 className="text-2xl font-heading font-bold text-luvcorp-navy">
                  OBRA<span className="text-luvcorp-gold">SUL</span>-RJ
                </h1>
                <p className="text-[10px] text-luvcorp-navy/50 tracking-wider">Obra Padrão · Sistema de Gestão de Obras</p>
              </div>
            </div>
          </div>

          <Card className="p-2">
            <CardHeader className="space-y-1 pb-4">
              <CardTitle className="text-2xl font-heading text-luvcorp-navy">Bem-vindo</CardTitle>
              <CardDescription className="text-luvcorp-navy/55">
                Faça login para acessar o sistema
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleLogin} className="space-y-5">
                <div className="space-y-2">
                  <Label htmlFor="login-email" className="text-luvcorp-navy/80 text-sm font-medium">Email</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-luvcorp-navy/40 z-10" />
                    <Input
                      id="login-email"
                      type="email"
                      placeholder="seu@email.com"
                      value={loginEmail}
                      onChange={(e) => setLoginEmail(e.target.value)}
                      className="pl-10"
                      required
                      data-testid="login-email-input"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="login-password" className="text-luvcorp-navy/80 text-sm font-medium">Senha</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-luvcorp-navy/40 z-10" />
                    <Input
                      id="login-password"
                      type="password"
                      placeholder="••••••••"
                      value={loginPassword}
                      onChange={(e) => setLoginPassword(e.target.value)}
                      className="pl-10"
                      required
                      data-testid="login-password-input"
                    />
                  </div>
                </div>
                <Button
                  type="submit"
                  variant="default"
                  className="w-full h-11 text-base"
                  disabled={isLoading}
                  data-testid="login-submit-btn"
                >
                  {isLoading ? "Entrando..." : "Entrar"}
                </Button>
              </form>
              
              {/* Security Notice */}
              <div className="mt-6 p-4 neu-inset rounded-neu-md">
                <div className="flex items-start gap-3">
                  <Shield className="h-5 w-5 text-luvcorp-gold flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-luvcorp-navy">Acesso Restrito</p>
                    <p className="text-xs text-luvcorp-navy/55 mt-1">
                      Novos usuários devem ser cadastrados pelo administrador do sistema.
                    </p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <p className="text-center text-xs text-luvcorp-navy/40 mt-6">
            © 2026 OBRASUL-RJ · Obra Padrão · Sistema de Gestão de Obras. Todos os direitos reservados.
          </p>
        </div>
      </div>
    </div>
  );
}
